import React from "react";
import * as Yup from "yup";
import styled from "styled-components";
import { ErrorMessage, Field, Form, Formik } from "formik";
import classNames from "classnames";
import { Col, Row, Spinner } from "reactstrap";
import CustomCheckbox from "components/customs/checkbox";
import CustomSelect from "components/customs/dropdown";
import { Check } from "react-feather";
import PasswordInput from "components/customs/password-input/PasswordInput";
import { useNavigate } from "react-router";
import FormGenerator from "components/customs/form-generator/input";
import { addProjectDropdownStyles } from "constants/style";
import {
  ACCOUNT_AGENCY,
  ACCOUNT_AGENCY_ID,
  ACCOUNT_COS_ID,
  ACCOUNT_CONGRESSMAN_ID,
  ALL_OPTION,
  ACCOUNT_DIRECTOR_ID,
  ACCOUNT_COMMITTEE_ID,
} from "utility/constants";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { ADD_ACTION, EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import {
  FormLabel,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import DropdownSearch from "components/customs/dropdownSearch";
import {
  EMAIL_REGEX,
  emailValidationSchema,
  mobileValidationSchema,
} from "validation";
import { getFormattedContactNumber } from "utility/helpers";
import { ALL_AGENCY_OPTION, ALL_PROGRAM_OPTION } from "./utility";

const PasswordChangeInfo = styled.div`
  border-top: solid #80808080 1px;
  margin-top: 1rem;
  padding-top: 1rem;

  --bs-text-opacity: 1;
  color: rgba(var(--bs-danger-rgb), var(--bs-text-opacity)) !important;
`;

const FormRow = styled(Row)`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
`;

const DIRECTOR_VIEW = [
  ACCOUNT_DIRECTOR_ID,
  ACCOUNT_AGENCY_ID,
  ACCOUNT_COMMITTEE_ID,
];

const CONGRESSMAN_VIEW = [ACCOUNT_CONGRESSMAN_ID, ACCOUNT_COS_ID];

const validSchema = emailValidationSchema.concat(mobileValidationSchema).shape({
  accountType: Yup.object().required("Required"),
  lastname: Yup.string().when("accountType", {
    is: (accountType) => accountType?.value !== ACCOUNT_CONGRESSMAN_ID,
    then: () => Yup.string().required("Required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  firstname: Yup.string().when("accountType", {
    is: (accountType) => accountType?.value !== ACCOUNT_CONGRESSMAN_ID,
    then: () => Yup.string().required("Required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  congressman: Yup.object().when("accountType", {
    is: (accountType) => CONGRESSMAN_VIEW.includes(accountType?.value),
    then: () => Yup.object().required("Required"),
    otherwise: () => Yup.object().notRequired(),
  }),
  agency: Yup.object().when(["accountType", "sendCreds"], {
    is: (accountType, sendCreds) =>
      sendCreds === true && accountType?.value === ACCOUNT_AGENCY_ID,
    then: () => Yup.object().required("Required"),
    otherwise: () => Yup.object().notRequired(),
  }),
  programs: Yup.array().when(["accountType", "sendCreds"], {
    is: (accountType, sendCreds) =>
      sendCreds === true && accountType?.value === ACCOUNT_AGENCY_ID,
    then: () => Yup.array().of(Yup.object().required("Required")),
    otherwise: () => Yup.array().of(Yup.object().notRequired()),
  }),
  password: Yup.string().when("sendCreds", {
    is: (sendCreds) => sendCreds === true,
    then: () =>
      Yup.string()
        .matches(/(?=.*?[A-Z])/, "At least one Uppercase")
        .matches(/(?=.*?[a-z])/, "At least one Lowercase")
        .matches(/(?=.*?[0-9])/, "At least one Digit")
        .matches(/(?=.*?[#?!@$%^&*-])/, "At least one Special Character")
        .matches(/.{8,}/, "At least 8 characters")
        .required("Required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  passwordConfirmation: Yup.string().when("sendCreds", {
    is: (sendCreds) => sendCreds === true,
    then: () =>
      Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Required"),
    otherwise: () => Yup.string().notRequired(),
  }),
  changeEmail: Yup.string()
    .matches(EMAIL_REGEX, "Invalid email format")
    .notRequired(),
});

const AccountForm = ({
  action,
  selectedAccountType,
  handleOnSubmit,
  formFields,
  setSelectedAccountType,
  accountTypeOptions,
  handleCongressmanSearch,
  setFormFields,
  agencyOptions,
  isLoading,
  agenciesOptions,
  programOptions,
  agencyPrograms,
}) => {
  const { user } = useAuth();
  const role = user?.type;

  const navigate = useNavigate();

  const isAgencySelectionShown =
    selectedAccountType?.value === ACCOUNT_AGENCY_ID;

  const isCongressmanSelectionShown =
    selectedAccountType?.value === ACCOUNT_COS_ID ||
    selectedAccountType?.value === ACCOUNT_CONGRESSMAN_ID;

  const isDirectorSelectionShown =
    selectedAccountType && !DIRECTOR_VIEW.includes(selectedAccountType?.value);

  const onAgencyChange = (options, values) => {
    let newOptions =
      options?.map((op) => ({
        id: op.id,
        value: op.id,
        label: op.acronym,
        acronym: op.acronym,
      })) || [];

    if (
      newOptions.length > 1 &&
      newOptions.some((agency) => agency.value === ALL_OPTION)
    ) {
      newOptions = [ALL_AGENCY_OPTION];
    }

    if (
      agencyOptions.length > 0 &&
      newOptions.length > 0 &&
      newOptions.length === agencyOptions.length
    ) {
      newOptions = [ALL_AGENCY_OPTION];
    }

    setFormFields({
      ...values,
      agencies: newOptions,
    });
  };

  const onProgramChange = (options, values) => {
    let newOptions =
      options?.map((op) => ({
        id: op.value,
        value: op.value,
        label: op.label,
      })) || [];

    if (
      newOptions.length > 1 &&
      newOptions.some((program) => program.value === ALL_OPTION)
    ) {
      newOptions = [ALL_PROGRAM_OPTION];
    }

    if (
      agencyPrograms.length > 0 &&
      newOptions.length > 0 &&
      newOptions.length === agencyPrograms.length
    ) {
      newOptions = [ALL_PROGRAM_OPTION];
    }

    setFormFields({
      ...values,
      programs: newOptions,
    });
  };

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Formik
      validationSchema={validSchema}
      onSubmit={handleOnSubmit}
      initialValues={formFields}
      enableReinitialize
    >
      {({ handleChange, values, setFieldValue }) => (
        <Form autoComplete="off">
          <Field name="accountType">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Account Type"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Agency Type"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <CustomSelect
                    name="accountType"
                    placeholder="Account Type"
                    styles={addProjectDropdownStyles}
                    onChange={(option) => {
                      setFieldValue("accountType", option);
                      setSelectedAccountType(option);
                    }}
                    className={classNames({
                      "is-invalid": errors?.accountType && touched?.accountType,
                    })}
                    options={accountTypeOptions}
                    value={values.accountType}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="accountType">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          {isDirectorSelectionShown && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Agencies"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Select agencies"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomSelect
                  placeholder=""
                  name="agencies"
                  isMulti
                  styles={addProjectDropdownStyles}
                  options={
                    values?.agencies?.some(
                      (project) => project.value === ALL_OPTION
                    )
                      ? []
                      : agenciesOptions
                  }
                  value={values.agencies}
                  onChange={(options) => onAgencyChange(options, values)}
                  getOptionLabel={(option) => {
                    return option?.acronym;
                  }}
                  filterOption={(option, searchValue) =>
                    option?.label
                      ?.toLowerCase()
                      ?.includes(searchValue?.toLowerCase()) ||
                    option?.data?.acronym
                      ?.toLowerCase()
                      ?.includes(searchValue?.toLowerCase())
                  }
                  isDisabled={action === VIEW_ACTION}
                />
              </Col>
            </FormRow>
          )}

          {isCongressmanSelectionShown && (
            <Field name="congressman">
              {({ form: { touched, errors } }) => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Congressman"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Search for an existing Congressman"}</p>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <DropdownSearch
                      placeholder=""
                      styles={addProjectDropdownStyles}
                      onChange={(option) => {
                        setFieldValue("congressman", option);

                        if (values.accountType?.value === ACCOUNT_COS_ID) {
                          setFieldValue(
                            "firstname",
                            option.cos.firstName || option.cos.name
                          );

                          setFieldValue("lastname", option.cos.lastName);

                          setFieldValue(
                            "mobile",
                            getFormattedContactNumber(option.cos.contact)
                          );
                        }

                        if (
                          values.accountType?.value === ACCOUNT_CONGRESSMAN_ID
                        ) {
                          setFieldValue("lastname", option.lastName);
                          setFieldValue("firstname", option.firstName);

                          setFieldValue(
                            "mobile",
                            getFormattedContactNumber(option.contact)
                          );
                        }
                      }}
                      name="congressman"
                      className={classNames({
                        "is-invalid":
                          errors?.congressman && touched?.congressman,
                      })}
                      loadOptions={handleCongressmanSearch}
                      value={values.congressman}
                      isDisabled={action !== ADD_ACTION}
                    />
                    <ErrorMessage name="congressman">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          {selectedAccountType?.value !== ACCOUNT_CONGRESSMAN_ID ||
          action !== ADD_ACTION ? (
            <>
              <Field name="lastname">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Last Name"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Provide your Last Name"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormGenerator
                        type="text"
                        name="lastname"
                        autoComplete="off"
                        className={classNames({
                          "is-invalid": errors?.lastname && touched?.lastname,
                        })}
                        value={values.lastname}
                        onChange={(evt) => {
                          handleChange(evt);
                        }}
                      />

                      <ErrorMessage name="lastname">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>

              <Field name="firstname">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"First Name"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Provide your First Name"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormGenerator
                        type="text"
                        name="firstname"
                        autoComplete="off"
                        className={classNames({
                          "is-invalid": errors?.firstname && touched?.firstname,
                        })}
                        value={values.firstname}
                        onChange={(evt) => {
                          handleChange(evt);
                        }}
                      />
                      <ErrorMessage name="firstname">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>
            </>
          ) : null}

          <Field name="email">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Email Address"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Email Address"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="text"
                    name="email"
                    className={classNames({
                      "is-invalid": errors?.email && touched?.email,
                    })}
                    value={values.email}
                    onChange={(evt) => {
                      handleChange(evt);
                    }}
                    disabled={action === EDIT_ACTION}
                    autoComplete="off"
                  />
                  <ErrorMessage name="email">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="mobile">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Mobile Number"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Mobile Number"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    name="mobile"
                    type="mobile"
                    className={classNames({
                      "is-invalid": errors?.mobile && touched?.mobile,
                    })}
                    value={values.mobile || ""}
                    onChange={(value) => {
                      setFieldValue("mobile", value);
                    }}
                  />
                  <ErrorMessage name="mobile">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          {isAgencySelectionShown ? (
            <>
              <Field name="agency">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Agency"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Provide the name of the Agency"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <CustomSelect
                        placeholder="Agency"
                        name="agency"
                        isDisabled={
                          role === ACCOUNT_AGENCY ||
                          selectedAccountType?.value !== 2
                        }
                        className={classNames({
                          "is-invalid": errors?.agency && touched?.agency,
                        })}
                        styles={addProjectDropdownStyles}
                        options={agencyOptions}
                        value={values.agency}
                        maxMenuHeight="200px"
                        onChange={(option) => {
                          setFieldValue("agency", option);

                          setFormFields({
                            ...values,
                            agency: option,
                          });
                        }}
                        filterOption={(option, searchValue) =>
                          option?.label
                            ?.toLowerCase()
                            ?.includes(searchValue?.toLowerCase()) ||
                          option?.data?.acronym
                            ?.toLowerCase()
                            ?.includes(searchValue?.toLowerCase())
                        }
                      />
                      <ErrorMessage name="agency">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>

              <Field name="programs">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Programs"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Select program/s under the Agency"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <CustomSelect
                        placeholder=""
                        name="programs"
                        isMulti
                        isDisabled={!values.agency}
                        styles={addProjectDropdownStyles}
                        options={
                          values?.programs?.some(
                            (op) => op.value === ALL_OPTION
                          )
                            ? []
                            : programOptions
                        }
                        className={classNames({
                          "is-invalid": errors?.programs && touched?.programs,
                        })}
                        value={values.programs}
                        onChange={(options) => onProgramChange(options, values)}
                      />

                      <ErrorMessage name="programs">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>
            </>
          ) : null}

          {action !== VIEW_ACTION && (
            <>
              {action === EDIT_ACTION && (
                <PasswordChangeInfo>
                  {
                    "Do not add email and/or password if you don't want to change user's email and/or password"
                  }
                </PasswordChangeInfo>
              )}

              {/* Fake fields to catch auto-fill of email and password */}
              <input
                type="text"
                name="fake_username"
                autoComplete="username"
                tabIndex="-1"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  border: "none",
                }}
              />
              <input
                type="password"
                name="fake_password"
                autoComplete="new-password"
                tabIndex="-1"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  border: "none",
                }}
              />

              {action === EDIT_ACTION && (
                <Field name="changeEmail">
                  {({ form: { touched, errors } }) => (
                    <FormRow>
                      <Col
                        xs="12"
                        md="6"
                      >
                        <FormLabel>
                          {"New Email Address"}
                          {RequiredIndicator}
                        </FormLabel>
                        <p>{"Type in your new email"}</p>
                      </Col>
                      <Col
                        xs="12"
                        md="6"
                      >
                        <FormGenerator
                          type="text"
                          name="changeEmail"
                          className={classNames({
                            "is-invalid":
                              errors?.changeEmail && touched?.changeEmail,
                          })}
                          value={values.changeEmail}
                          onChange={(evt) => {
                            handleChange(evt);
                          }}
                          disabled={action === VIEW_ACTION}
                          autoComplete="off"
                        />
                        <ErrorMessage name="changeEmail">
                          {(msg) => (
                            <small className="field-error text-danger">
                              {msg}
                            </small>
                          )}
                        </ErrorMessage>
                      </Col>
                    </FormRow>
                  )}
                </Field>
              )}

              <Field name="password">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Password"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Type in your Password"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <PasswordInput
                        type="password"
                        name="password"
                        value={values.password}
                        className={classNames({
                          "is-invalid": errors?.password && touched?.password,
                        })}
                        onChange={(evt) => {
                          handleChange(evt);
                        }}
                        autoComplete="off"
                      />
                      <ErrorMessage name="password">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>

              <Field name="passwordConfirmation">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Confirm Password"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Re-type your Password"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <PasswordInput
                        type="password"
                        name="passwordConfirmation"
                        value={values.passwordConfirmation}
                        className={classNames({
                          "is-invalid":
                            errors?.passwordConfirmation &&
                            touched?.passwordConfirmation,
                        })}
                        onChange={(evt) => {
                          handleChange(evt);
                        }}
                      />
                      <ErrorMessage name="passwordConfirmation">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>
            </>
          )}

          {action === EDIT_ACTION && (
            <Field name="status">
              {() => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>{"Archive Account?"}</FormLabel>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <CustomCheckbox
                      name="status"
                      checked={values.status}
                      icon={
                        <Check
                          className="vx-icon"
                          size={16}
                        />
                      }
                      onChange={(evt) => {
                        handleChange(evt);
                      }}
                      type="checkbox"
                    />
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          {action === ADD_ACTION && (
            <Field name="sendCreds">
              {() => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>{"Send Credentials via SMS?"}</FormLabel>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <CustomCheckbox
                      name="sendCreds"
                      checked={values.sendCreds}
                      disabled={action !== ADD_ACTION}
                      icon={
                        <Check
                          className="vx-icon"
                          size={16}
                        />
                      }
                      onChange={(evt) => {
                        handleChange(evt);
                      }}
                      type="checkbox"
                    />
                    <ErrorMessage name="sendCreds">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          <ButtonsContainer>
            {action !== VIEW_ACTION && (
              <StyledCancelButton
                disabled={isLoading}
                outline
                onClick={() => navigate("/accounts")}
              >
                {"Cancel"}
              </StyledCancelButton>
            )}
            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </ButtonsContainer>
        </Form>
      )}
    </Formik>
  );
};

export default AccountForm;
